import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Alert, Spinner, cx } from '../../components/ui.tsx';
import { FileTypeIcon } from '../../components/icons.tsx';
import { formatBytes, formatRelative, pluralise } from '../../lib/display.ts';

/**
 * Where the room went.
 *
 * The dashboard says how much is stored; this says what is storing it and
 * what to do about it, which is the question anybody actually opens a storage
 * page to ask. So the biggest things are the page — one list, every kind of
 * record in it, largest first — and the totals are a band across the top
 * rather than the point.
 *
 * The bar measures against the disk the files are on, read from the disk
 * itself. Nobody has to keep a quota figure true, and a server given a bigger
 * volume says so the next time this page is opened.
 */

interface StorageUsage {
  disk: { capacity: string; free: string } | null;
  used: { files: string; spreadsheets: string; documents: string; total: string };
  saved: string;
  trashed: string;
  counts: { files: number; spreadsheets: number; documents: number };
}

type Kind = 'file' | 'spreadsheet' | 'document';

interface StorageItem {
  kind: Kind;
  id: string;
  name: string;
  path: string;
  bytes: string;
  updatedAt: string;
  mimeType?: string;
  trashed: boolean;
}

/** The three kinds, in the order they appear in the bar and the legend. */
const SEGMENTS: { kind: Kind; label: string; colour: string; of: keyof StorageUsage['used'] }[] = [
  { kind: 'file', label: 'Files', colour: 'var(--blue-600, #2563eb)', of: 'files' },
  { kind: 'spreadsheet', label: 'Sheets', colour: '#16a34a', of: 'spreadsheets' },
  { kind: 'document', label: 'Documents', colour: '#f59e0b', of: 'documents' },
];

export function StoragePage() {
  const [kind, setKind] = useState<Kind | 'all'>('all');

  const usage = useQuery({
    queryKey: ['storage'],
    queryFn: () => api.get<StorageUsage>('/api/storage'),
    staleTime: 30_000,
  });

  const items = useQuery({
    queryKey: ['storage', 'items'],
    queryFn: () => api.get<StorageItem[]>('/api/storage/items?limit=200'),
    staleTime: 30_000,
  });

  const shown = useMemo(
    () => (items.data ?? []).filter((item) => kind === 'all' || item.kind === kind),
    [items.data, kind],
  );

  if (usage.isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 text-body-sm text-muted">
        <Spinner /> Loading…
      </div>
    );
  }

  if (usage.isError || !usage.data) return <Alert>Could not read the storage figures.</Alert>;

  const data = usage.data;
  const used = Number(data.used.total);
  const capacity = data.disk ? Number(data.disk.capacity) : 0;

  return (
    <div className="h-full overflow-y-auto">
      <header className="border-b border-border pb-4">
        <h1 className="text-h1 text-ink">Storage</h1>
        <p className="mt-0.5 text-meta text-muted">
          {pluralise(data.counts.files, 'file')} · {pluralise(data.counts.spreadsheets, 'sheet')} ·{' '}
          {pluralise(data.counts.documents, 'document')}
        </p>
      </header>

      <section className="mt-5">
        <p className="text-[26px] font-semibold leading-tight text-ink">
          {formatBytes(used)}
          {capacity > 0 ? (
            <span className="text-body text-muted"> of {formatBytes(capacity)} used</span>
          ) : (
            <span className="text-body text-muted"> stored</span>
          )}
        </p>

        <Bar usage={data} capacity={capacity} used={used} />

        <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          {SEGMENTS.map((segment) => (
            <button
              key={segment.kind}
              type="button"
              onClick={() => setKind(kind === segment.kind ? 'all' : segment.kind)}
              className={cx(
                'flex items-center gap-2 rounded px-1.5 py-0.5 text-meta transition-fast hover:bg-sunken',
                kind === segment.kind ? 'text-ink' : 'text-muted',
              )}
            >
              <span
                className="h-2.5 w-2.5 rounded-full"
                style={{ background: segment.colour }}
                aria-hidden
              />
              {segment.label}
              <span className="text-subtle">{formatBytes(Number(data.used[segment.of]))}</span>
            </button>
          ))}

          {data.disk ? (
            <span className="flex items-center gap-2 px-1.5 text-meta text-muted">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--border-strong)]" aria-hidden />
              Free on the disk
              <span className="text-subtle">{formatBytes(Number(data.disk.free))}</span>
            </span>
          ) : null}
        </div>

        {/* The two numbers that are worth acting on, and only when they are
            worth acting on: nothing to reclaim means nothing to say. */}
        <div className="mt-3 flex flex-wrap gap-2">
          {Number(data.trashed) > 0 ? (
            <Link
              to="/trash"
              className="rounded-md border border-border bg-surface px-2.5 py-1 text-meta text-ink transition-fast hover:bg-sunken"
            >
              {formatBytes(Number(data.trashed))} in the trash — empty it to get the space back
            </Link>
          ) : null}
          {Number(data.saved) > 0 ? (
            <span className="rounded-md border border-border bg-surface px-2.5 py-1 text-meta text-muted">
              {formatBytes(Number(data.saved))} saved by storing duplicate files once
            </span>
          ) : null}
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-border-strong bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2.5">
          <h2 className="text-h3 text-ink">Largest first</h2>
          <div className="flex items-center gap-1">
            <Filter label="All" active={kind === 'all'} onClick={() => setKind('all')} />
            {SEGMENTS.map((segment) => (
              <Filter
                key={segment.kind}
                label={segment.label}
                active={kind === segment.kind}
                onClick={() => setKind(segment.kind)}
              />
            ))}
          </div>
        </div>

        {items.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-body-sm text-muted">
            <Spinner /> Measuring…
          </p>
        ) : shown.length === 0 ? (
          <p className="px-4 py-8 text-center text-body-sm text-muted">Nothing stored yet.</p>
        ) : (
          <ul>
            {shown.map((item) => (
              <Row key={`${item.kind}-${item.id}`} item={item} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

/**
 * The bar: one length per kind, and the rest of the disk behind them.
 *
 * Scaled against the disk when its size is known and against the total when
 * it is not — a bar with no end cannot say "nearly full", so in that case it
 * shows the proportions between the three kinds and nothing about headroom.
 */
function Bar({ usage, capacity, used }: { usage: StorageUsage; capacity: number; used: number }) {
  const scale = capacity > 0 ? capacity : Math.max(1, used);

  return (
    <div className="mt-3 flex h-2.5 w-full overflow-hidden rounded-full bg-[var(--surface-sunken)]">
      {SEGMENTS.map((segment) => {
        const bytes = Number(usage.used[segment.of]);
        if (bytes <= 0) return null;
        return (
          <span
            key={segment.kind}
            title={`${segment.label}: ${formatBytes(bytes)}`}
            style={{
              width: `${Math.max(0.4, (bytes / scale) * 100)}%`,
              background: segment.colour,
            }}
          />
        );
      })}
    </div>
  );
}

function Filter({ label, active, onClick }: { label: string; active: boolean; onClick(): void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'rounded-full border px-2.5 py-0.5 text-meta transition-fast',
        active
          ? 'border-[var(--blue-600)] bg-[var(--blue-100)] text-[var(--blue-800)]'
          : 'border-border text-muted hover:bg-sunken',
      )}
    >
      {label}
    </button>
  );
}

/** Where a row goes when it is clicked: the thing itself, or the Drive. */
function hrefFor(item: StorageItem): string {
  if (item.trashed) return '/trash';
  if (item.kind === 'spreadsheet') return `/sheets/${item.id}`;
  if (item.kind === 'document') return `/documents/${item.id}`;
  return '/drive';
}

function Row({ item }: { item: StorageItem }) {
  const kindLabel =
    item.kind === 'spreadsheet' ? 'Sheet' : item.kind === 'document' ? 'Document' : 'File';

  return (
    <li>
      <Link
        to={hrefFor(item)}
        className="flex items-center gap-3 border-b border-border px-4 py-2 transition-fast last:border-0 hover:bg-blue-50"
      >
        <FileTypeIcon
          name={item.name}
          {...(item.mimeType ? { mimeType: item.mimeType } : {})}
          className="h-5 w-5 shrink-0"
        />

        <span className="min-w-0 flex-1">
          <span className="block truncate text-body-sm text-ink">{item.name}</span>
          <span className="block truncate text-micro text-muted">
            {kindLabel}
            {item.path ? ` · ${item.path}` : ''}
            {item.trashed ? ' · in the trash' : ''}
          </span>
        </span>

        <span className="hidden shrink-0 text-micro text-muted sm:block">
          {formatRelative(item.updatedAt)}
        </span>
        <span className="w-20 shrink-0 text-right text-body-sm tabular-nums text-ink">
          {formatBytes(Number(item.bytes))}
        </span>
      </Link>
    </li>
  );
}
